import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminGallery.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import Notification from '../../components/Notification/Notification';

const AdminGallery = () => {
    const navigate = useNavigate();

    const [gallery, setGallery] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ type: '', message: '' });

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        message: '',
        onConfirm: null,
        loading: false,
    });

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification({ type: '', message: '' }), 4000);
    };

    // Fetch gallery items
    const fetchGallery = async () => {
        try {
            setLoading(true);
            const res = await privateAxios.get('/private/admin-gallery');
            if (res.status === 200) {
                setGallery(res.data.gallery);
            }
        } catch (err) {
            console.error(err);
            setGallery([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGallery();
    }, []);

    const toggleSelect = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const deleteSelected = () => {
        if (!selectedItems?.length) return;

        setConfirmDialog({
            open: true,
            message: `Delete ${selectedItems?.length} selected gallery item${selectedItems?.length > 1 ? 's' : ''}?`,
            onConfirm: async () => {
                try {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    await privateAxios.post('/private/delete-selected-gallery', {
                        ids: selectedItems,
                    });

                    setSelectedItems([]);
                    setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
                    showNotification('success', 'Selected gallery items deleted.');
                    fetchGallery();
                } catch (err) {
                    console.error(err);
                    setConfirmDialog((prev) => ({ ...prev, loading: false }));
                }
            },
            loading: false,
        });
    };

    const deleteSingle = (id, title) => {
        setConfirmDialog({
            open: true,
            message: `Delete gallery item titled "${title}"?`,
            onConfirm: async () => {
                try {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    await privateAxios.delete(`/private/delete-gallery-image/${id}`);

                    setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
                    showNotification('success', 'Gallery item deleted.');
                    fetchGallery();
                } catch (err) {
                    console.error(err);
                    setConfirmDialog((prev) => ({ ...prev, loading: false }));
                }
            },
            loading: false,
        });
    };

    const handleEdit = (slug) => navigate(`/admin-edit-gallery/${slug}`);

    return (
        <div className="admin-gallery">
            <Notification {...notification} onClose={() => setNotification({ type: '', message: '' })} />

            <div className="admin-gallery-header">
                <h2>Manage Gallery</h2>
                <Link to="/add-to-gallery">Add Image To Gallery</Link>

                {gallery?.length > 0 && (
                    <button
                        className="delete-selected-btn"
                        disabled={!selectedItems?.length}
                        onClick={deleteSelected}
                    >
                        Delete Selected
                    </button>
                )}
            </div>

            <div className="admin-gallery-list">
                {loading ? (
                    <div className="admin-gallery-loading">
                        <Circles height="50" width="50" color="#008751" />
                        <h3>Loading Gallery...</h3>
                    </div>
                ) : gallery?.length ? (
                    gallery?.map((item) => (
                        <div className="admin-gallery-card" key={item._id}>
                            <div className="gallery-checkbox-info">
                                <input
                                    type="checkbox"
                                    checked={selectedItems.includes(item._id)}
                                    onChange={() => toggleSelect(item._id)}
                                />

                                <div className="gallery-info">
                                    <h4>{item.title}</h4>
                                    <small>
                                        <p>
                                            Added by:{' '}
                                            <strong>
                                                {item.createdBy?.firstname} {item.createdBy?.lastname}
                                            </strong>
                                        </p>
                                        <p>
                                            On:{' '}
                                            <strong>
                                                {moment(item.createdAt).format('MMMM DD, YYYY')}
                                            </strong>
                                        </p>
                                    </small>
                                </div>
                            </div>

                            <div className="gallery-actions">
                                <button onClick={() => handleEdit(item.slug)}>
                                    <FaEdit />
                                </button>
                                <button onClick={() => deleteSingle(item._id, item.title)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p style={{ textAlign: 'center' }}>No gallery items available.</p>
                )}
            </div>

            <div className="back-to-dashboard">
                <Link to="/admin-dashboard">Back to Dashboard</Link>
            </div>

            {confirmDialog.open && (
                <ConfirmDialog
                    message={confirmDialog.message}
                    onConfirm={confirmDialog.onConfirm}
                    onCancel={() =>
                        setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })
                    }
                    loading={confirmDialog.loading}
                />
            )}
        </div>
    );
};

export default AdminGallery;